// useOnlinePresence — register as a project function so pages can call it
// via el.call('useOnlinePresence') from onCreate. Returns a cleanup
// function to call from onRemove.
//
// Every caller subscribes to a single shared channel (`presence:symbols`)
// so the CalOnlineUsersBar surface reflects presence across the entire
// editor and governance suite — calendar, tickets, documents, canvas,
// workspaces. The optional `app` tag is tracked as presence metadata for
// per-app breakdowns later; it no longer scopes the channel itself.
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.presence`
// dispatches through the SDK's transport-agnostic API. Today's transport
// is Supabase realtime via supabaseRealtimeProvider in bootShell; when
// backend ships native socket.io realtime alongside CollabService, swap
// the provider — this file doesn't change.

import { getSDK } from './sdk.js'

// One subscription + one cleanup per browser tab — no matter how many
// pages call useOnlinePresence, they all join the same shared presence
// stream instead of clobbering each other's `root.onlineUsers`.
let _cleanup = null

export function useOnlinePresence ({ app = 'symbols' } = {}) {
  const el = this
  const root = el.state.root
  const me = root.currentUserEmail
  if (!me) return () => {}
  if (_cleanup) return _cleanup

  let _unsub = null
  let _cancelled = false

  // Resolve the workspaceProject service via the SDK proxy and subscribe.
  // The proxy auto-awaits SDK init, so this works even when called early
  // in onCreate before init has settled.
  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribePresence) return
      _unsub = wp.realtime.subscribePresence(
        { scope: 'symbols', userKey: me, meta: { app } },
        (event, data) => {
          // The SDK provider forwards Supabase presence events as
          // ('sync'|'join'|'leave', payload). Re-derive the full online
          // list on each event so root.onlineUsers stays the canonical view.
          if (event === 'sync') {
            const state = data?.state || {}
            const seen = new Set()
            const list = []
            for (const key of Object.keys(state)) {
              if (seen.has(key)) continue
              seen.add(key)
              const meta = state[key]?.[0] || {}
              list.push({
                email: key,
                since: meta.online_at || Date.now(),
                app: meta.app || null,
              })
            }
            root.update({ onlineUsers: list })
          }
          // join/leave are followed by their own sync — no work needed here.
        }
      )
    })
    .catch((err) => {
      console.warn('[useOnlinePresence] subscribe failed:', err?.message || err)
    })

  _cleanup = () => {
    _cleanup = null
    _cancelled = true
    if (typeof _unsub === 'function') {
      try { _unsub() } catch (_) {}
    }
  }
  return _cleanup
}
